import Chart from 'chart.js/auto'
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle.min.js'

const MONTHS_IN_YEAR = 12
const TICKS_YEARS = 5

const TooltipRender = {
    activate(chart, { activeDatasetIndex = undefined }) {
        const tooltip = chart.tooltip
        const clearTooltips = () => {
            tooltip.setActiveElements([])
            chart.update()
        }
        const showTooltip = activeElement => {
            tooltip.setActiveElements([activeElement])
            chart.update()
        }

        setTimeout(() => {
            const dataSet = chart.data.datasets[activeDatasetIndex]?.data ||
                undefined
            const lastIndex = dataSet?.length

            clearTooltips()

            if (lastIndex !== undefined) {
                showTooltip(
                    { datasetIndex: activeDatasetIndex, index: lastIndex - 1 })
            }

        }, 300)

    },
}

class Locale {
    static IT = 'it-IT'
    static EN = 'en-US'

    static isValid(locale) {
        return [this.IT, this.EN].includes(locale)
    }
}

class DomUtils {
    static getAttributeFor(el, name) {
        return (el && el.getAttribute(name)) || undefined
    }

    static getLocale() {
        return this.getAttributeFor(document.querySelector('html'), 'lang')
    }
}

class NumberFormatter {
    static EURO_SYMBOL = '€'

    constructor(locale) {
        this.locale = locale
    }

    formatEuro(number, includeSymbol = true) {
        const prefix = includeSymbol && this.locale == Locale.EN
            ? NumberFormatter.EURO_SYMBOL + ' '
            : ''
        const suffix = includeSymbol && this.locale == Locale.IT ? ' ' +
            NumberFormatter.EURO_SYMBOL : ''

        return isNaN(number) ? '' : `${prefix}${number.toLocaleString(
            this.locale,
            { minimumFractionDigits: 0, maximumFractionDigits: 2 })}${suffix}`
    }
}

function createNumberFormatter() {
    const locale = DomUtils.getLocale()

    if (Locale.isValid(locale)) {
        return new NumberFormatter(locale)
    }

    console.error(`Number formatter, locale $(locale) not supported`)

    return {
        formatEuro: (number, includeSymbol = true) => number + ' ' +
            NumberFormatter.EURO_SYMBOL,
    }
}

class SliderRange {
    constructor(steps) {
        this.steps = steps
        this.value = steps[0] || 0
        this.label = undefined
    }

    get min() {
        return 0
    }

    get max() {
        return Math.max(this.steps.length - 1, 0)
    }

    static formatLabel(fmt, params) {
        if (!fmt) {
            return null
        }

        let label = fmt

        for (const key of Object.keys(params)) {
            label = label.replace('${' + key + '}', params[key] ?? '')
        }

        return label
    }

    mount(element) {
        if (!element || !this.steps.length) {
            return
        }

        const range = element.querySelector('input[type="range"].form-range')
        const labelForValue = element.querySelector(
            '[data-role="label-for-value"]')
        const lowerBound = element.querySelector('[data-role="lower-bound"]')
        const upperBound = element.querySelector('[data-role="upper-bound"]')
        const formatExpr = element.getAttribute('data-format')

        const labelYears = 'years-label'
        const yearsSingular = element.getAttribute('data-years-singular')
        const yearsPlural = element.getAttribute('data-years-plural')
        const labels = {}

        if (yearsSingular && yearsPlural) {
            labels[labelYears] = {
                singular: yearsSingular,
                plural: yearsPlural,
            }
        }

        const paramsForValue = (value) => {
            let params = {
                value,
            }

            if (labels[labelYears]) {
                params = {
                    ...params, [labelYears]: value > 1
                        ? labels[labelYears].plural
                        : labels[labelYears].singular,
                }
            }

            return params
        }

        const labelForLowerBound = SliderRange.formatLabel(formatExpr,
            paramsForValue(this.steps[0]))
        this.label = labelForLowerBound

        if (lowerBound) {
            lowerBound.innerText = labelForLowerBound
        }

        if (upperBound) {
            upperBound.innerText = SliderRange.formatLabel(formatExpr,
                paramsForValue(this.steps[this.steps.length - 1]))
        }

        if (range) {
            range.min = this.min
            range.max = this.max
            range.step = 1

            range.addEventListener('input', (e) => {
                const target = e.target

                this.value = this.steps[target.value] || 0

                if (labelForValue) {
                    const label = SliderRange.formatLabel(formatExpr,
                        paramsForValue(this.value)) ?? ''
                    labelForValue.innerText = label
                    this.label = label
                }
            })
        }
    }
}

class CalculatorGraph {
    static DATASET_BANK_ACCOUNT = 0
    static DATASET_BEEWISE = 1

    static POINT_RADIUS_BEEWISE = 8
    static POINT_RADIUS_BANK_ACCOUNT = 4

    constructor(numberFormatter) {
        this.numberFormatter = numberFormatter
        this.chart = undefined
        this.mountElement = undefined
    }

    static generateDataForBeewiseBalanced(
        initialInvestment, monthlyDeposit, projectionPrev) {

        const historicalReturn = 0.075379752537092
        const monthlyReturn = Math.pow((1 + historicalReturn), (1 / 12)) - 1

        return (projectionPrev * (1 + monthlyReturn)) + monthlyDeposit
    }

    static generateDataForBankAccount(
        initialInvestment, monthlyDeposit, month) {

        return initialInvestment + (month * monthlyDeposit)
    }

    setUpChartGlobals() {
        Chart.defaults.color = '#fff'
        Chart.defaults.font.family = '"Sohne",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans","Liberation Sans",sans-serif'
        Chart.defaults.font.size = 13
    }

    createConfig(context) {
        const gradientBeewise = context.createLinearGradient(0, 0, 0, 450)
        gradientBeewise.addColorStop(0, 'rgba(255, 209, 68, 1)')
        gradientBeewise.addColorStop(0.5, 'rgba(255, 209, 68, 0.3)')
        gradientBeewise.addColorStop(1, 'rgba(27, 30, 59, 0.1)')

        const gradientBankAcccount = context.createLinearGradient(0, 0, 0, 450)
        gradientBankAcccount.addColorStop(0, 'rgba(0, 180, 250, 1)')
        gradientBankAcccount.addColorStop(0.5, 'rgba(0, 180, 250, 0.3)')
        gradientBankAcccount.addColorStop(1, 'rgba(27, 30, 59, 0.1)')

        const formatEuro = this.numberFormatter.formatEuro.bind(
            this.numberFormatter)
        const dataSetMixin = {
            fill: true,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#fff',
            steppedLine: false,
        }

        return {
            type: 'line',
            data: {
                labels: [],
                datasets: [
                    {
                        data: [],
                        backgroundColor: gradientBankAcccount,
                        borderColor: '#00b4fa',
                        ...dataSetMixin,
                        tension: 0,
                        pointHoverRadius: CalculatorGraph.POINT_RADIUS_BANK_ACCOUNT,
                        pointRadius: [],
                    },
                    {
                        data: [],
                        backgroundColor: gradientBeewise,
                        borderColor: '#ff6b00',
                        ...dataSetMixin,
                        tension: 0.3,
                        pointHoverRadius: CalculatorGraph.POINT_RADIUS_BEEWISE,
                        pointRadius: [],
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        display: true,
                        grid: {
                            color: 'rgb(59, 63, 94)',
                        },
                        ticks: {
                            callback: function (value) {
                                return this.getLabelForValue(value) || undefined
                            },
                        },
                    },
                    y: {
                        display: true,
                        grid: {
                            color: 'rgb(59, 63, 94)',
                        },
                        beginAtZero: true,
                        min: 0,
                        ticks: {
                            callback: function (value) {
                                return formatEuro(value)
                            },
                            maxTicksLimit: 9,
                        },
                    },
                },
                plugins: {
                    title: {
                        display: false,
                    },
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        displayColors: false,
                        backgroundColor: 'rgba(0, 0, 0, 0)',
                        borderColor: 'rgb(255, 255, 255)',
                        bodyFont: {
                            size: 22,
                            weight: 'bold',
                        },
                        callbacks: {
                            label: function (context) {
                                let label = ''
                                if (context.parsed.y !== null) {
                                    label = formatEuro(context.parsed.y)
                                }
                                return label
                            },
                            title: function () {
                                return undefined
                            },
                        },
                    },
                },
            },
        }
    }

    createData(initialInvestment, monthlyDeposit, years) {
        const data = []
        const now = new Date()
        let endOfMonth = undefined
        let projectionPrevForBeewise = initialInvestment

        for (let month = 0; month <= years * MONTHS_IN_YEAR; ++month) {
            const beewise = month === 0 ? initialInvestment :
                CalculatorGraph.generateDataForBeewiseBalanced(
                    initialInvestment, monthlyDeposit, projectionPrevForBeewise)

            const bankAccount = CalculatorGraph.generateDataForBankAccount(
                initialInvestment, monthlyDeposit, month)

            projectionPrevForBeewise = beewise

            endOfMonth = new Date(now.getFullYear(), now.getMonth() + month + 1,
                0)

            data.
                push({
                    x: endOfMonth.toISOString().slice(0, 10),
                    beewise,
                    bankAccount,
                })
        }

        return data
    }

    updateConfig(config, data) {
        const dataSetBeewise = config.data.datasets[CalculatorGraph.DATASET_BEEWISE]
        const dataSetBankAccount = config.data.datasets[CalculatorGraph.DATASET_BANK_ACCOUNT]

        const labels = data.map((entry, index) => {
            const date = new Date(entry.x)
            const lastIndex = data.length - 1

            if ((index === 0) || (index === lastIndex) ||
                ((index % (TICKS_YEARS * MONTHS_IN_YEAR))) === 0) {
                return date.getFullYear()
            }

            return ''
        })

        const pointRadiusZeroPadding = '#'.repeat(
            Math.max(labels.length - 1, 0)).
            split('').
            map(_ => 0)

        config.data.labels = labels
        dataSetBeewise.data = data.map((entry) => entry.beewise)
        dataSetBeewise.pointRadius = [
            ...pointRadiusZeroPadding,
            CalculatorGraph.POINT_RADIUS_BEEWISE]
        dataSetBankAccount.data = data.map((entry) => entry.bankAccount)
        dataSetBankAccount.pointRadius = [
            ...pointRadiusZeroPadding,
            CalculatorGraph.POINT_RADIUS_BANK_ACCOUNT]
    }

    mount(
        canvas,
        { initialInvestment = 2000, monthlyDeposit = 500, years = 30 }) {

        this.setUpChartGlobals()

        const context = canvas.getContext('2d')
        const config = this.createConfig(context)
        const data = this.createData(initialInvestment, monthlyDeposit, years)

        this.updateConfig(config, data)

        this.chart = new Chart(canvas, config)
        this.mountElement = canvas

        this.fireProfitDataCreated(data, years)
    }

    recompute({ initialInvestment = 2000, monthlyDeposit = 500, years = 30 }) {
        if (!this.chart) {
            return
        }

        const data = this.createData(initialInvestment, monthlyDeposit, years)

        this.updateConfig(this.chart, data)

        this.chart.update()

        this.fireProfitDataCreated(data, years)
    }

    fireProfitDataCreated(data, years) {
        if (!this.mountElement) {
            return
        }

        const [lastEntry] = data.slice(-1)
        const beewiseProfit = lastEntry?.beewise

        this.mountElement.dispatchEvent(
            new CustomEvent('graph:profit-data:created',
                { detail: { profit: { beewise: beewiseProfit, years } } }))
    }
}

export function initPortfolioCalculator() {
    const sliderSteps = {
        initialInvestment: [
            10, 100, 500, 1000, 2000,
        ],
        monthlyInvestment: [
            0, 25, 50, 100, 200, 500,
        ],
        years: [
            1, 5, 10, 20, 30,
        ],
    }
    const numberFormatter = createNumberFormatter()
    const calculatorCover = document.getElementById(
        'portfolio-calculator-cover')
    const calculatorGraph = document.getElementById(
        'portfolio-calculator-graph')

    if (!calculatorCover || !calculatorGraph) {
        return
    }

    const initialInvestmentSlider = new SliderRange(
        sliderSteps.initialInvestment)
    const monthlyInvestmentSlider = new SliderRange(
        sliderSteps.monthlyInvestment)
    const yearsSlider = new SliderRange(sliderSteps.years)

    const calculatorCoverInputs = calculatorCover.querySelector(
        '.calculator')

    if (calculatorCoverInputs) {
        initialInvestmentSlider.mount(
            calculatorCoverInputs.querySelector(
                '[data-role="initial-investment"]'))
        monthlyInvestmentSlider.mount(
            calculatorCoverInputs.querySelector(
                '[data-role="monthly-investment"]'))
        yearsSlider.mount(
            calculatorCoverInputs.querySelector('[data-role="years"]'))
    }

    const activateCalculatorGraph = calculatorCover.querySelector(
        '[data-role="activate-calculator-graph"]')
    const profitsCaptions = document.querySelectorAll(
        '[data-role="profits-caption"]')
    const redrawCalculatorGraph = calculatorGraph.querySelector(
        '[data-role="redraw-calculator-graph"]')
    const calculatorGraphInputs = calculatorGraph.querySelector(
        '[data-role="calculator-graph-inputs"]')
    const calculatorGraphModal = document.getElementById(
        'calculator-graph-modal')
    const calculatorGraphContainerSm = calculatorGraphModal.querySelector(
        '#calculator-graph-container-sm')
    const calculatorGraphCanvas = calculatorGraph.querySelector(
        '#calculator-graph-canvas')
    const labelForInitialInvestment = calculatorGraphModal.querySelector(
        '[data-role="initial-investment"]')
    const labelForMonthlyDeposit = calculatorGraphModal.querySelector(
        '[data-role="monthly-deposit"]')
    const labelForYears = calculatorGraphModal.querySelector(
        '[data-role="years"]')

    const mediaQueryBreakpointDownSm = window.matchMedia(
        'only screen and (max-width: 760px)')

    const labelsForCalculatorInputs = [
        {
            label: labelForInitialInvestment,
            slider: initialInvestmentSlider,
        },
        {
            label: labelForMonthlyDeposit,
            slider: monthlyInvestmentSlider,
        },
        {
            label: labelForYears,
            slider: yearsSlider,
        },
    ]

    const graph = new CalculatorGraph(numberFormatter)

    if (activateCalculatorGraph &&
        calculatorGraphInputs &&
        redrawCalculatorGraph &&
        calculatorGraphModal &&
        calculatorGraphContainerSm &&
        calculatorGraphCanvas) {

        if (mediaQueryBreakpointDownSm.matches) {
            calculatorGraphContainerSm.appendChild(calculatorGraphCanvas)
        }

        calculatorGraphCanvas.addEventListener('graph:profit-data:created',
            (e) => {
                console.log(profitsCaptions)

                const { beewise, years } = e.detail.profit
                const replacePlaceholders = (text, placeholders) => {
                    for (const key in placeholders) {
                        text = text.replace(`\${${key}}`, placeholders[key])
                    }
                    return text
                }

                profitsCaptions.forEach(profitsCaption => {
                    const label = DomUtils.getAttributeFor(profitsCaption,
                        'data-label') || ''
                    const yearsInLetter = {
                        singular: DomUtils.getAttributeFor(profitsCaption,
                            'data-years-singular') || '',
                        plural: DomUtils.getAttributeFor(profitsCaption,
                            'data-years-plural') || '',
                    }

                    profitsCaption.innerHTML = replacePlaceholders(label, {
                        'years_number': years,
                        'years_letter': years === 1
                            ? yearsInLetter.singular
                            : yearsInLetter.plural,
                        'profits': numberFormatter.formatEuro(beewise),
                    })
                })
            })

        activateCalculatorGraph.addEventListener('click', (e) => {
            e.preventDefault()

            for (const input of labelsForCalculatorInputs) {
                if (input.label) {
                    input.label.innerText = input.slider.label
                }
            }

            if (graph.chart === undefined) {
                graph.mount(calculatorGraphCanvas,
                    {
                        initialInvestment: initialInvestmentSlider.value,
                        monthlyDeposit: monthlyInvestmentSlider.value,
                        years: yearsSlider.value,
                    })
            }
            else {
                graph.recompute({
                    initialInvestment: initialInvestmentSlider.value,
                    monthlyDeposit: monthlyInvestmentSlider.value,
                    years: yearsSlider.value,
                })
            }

            TooltipRender.activate(graph.chart,
                { activeDatasetIndex: CalculatorGraph.DATASET_BEEWISE })

            if (mediaQueryBreakpointDownSm.matches) {
                if (calculatorGraphModal) {
                    const modal = new bootstrap.Modal(calculatorGraphModal,
                        { keyboard: false, focus: true })
                    modal.show()
                }
            }
            else {
                calculatorCover.classList.add('d-none')
                calculatorGraph.classList.remove('d-none')
                calculatorGraphInputs.appendChild(calculatorCoverInputs)
            }
        })

        redrawCalculatorGraph.addEventListener('click', (e) => {
            e.preventDefault()

            graph.recompute({
                initialInvestment: initialInvestmentSlider.value,
                monthlyDeposit: monthlyInvestmentSlider.value,
                years: yearsSlider.value,
            })

            TooltipRender.activate(graph.chart,
                { activeDatasetIndex: CalculatorGraph.DATASET_BEEWISE })
        })
    }
}
